import { UserFriendlyError } from '@affine/error';

export const SIGN_IN_ERROR_CODES = {
  APPLE_PRIVATE_RELAY_EMAIL_NOT_ALLOWED:
    'APPLE_PRIVATE_RELAY_EMAIL_NOT_ALLOWED',
  AUTH_UNEXPECTED_ERROR: 'AUTH_UNEXPECTED_ERROR',
  GRACE_PERIOD_EXPIRED: 'GRACE_PERIOD_EXPIRED',
  INVALID_CALLBACK_PARAMETERS: 'INVALID_CALLBACK_PARAMETERS',
  INVALID_MAGIC_LINK: 'INVALID_MAGIC_LINK',
  INVALID_OAUTH_CALLBACK_PARAMETERS: 'INVALID_OAUTH_CALLBACK_PARAMETERS',
  INVALID_OAUTH_PARAMETERS: 'INVALID_OAUTH_PARAMETERS',
  INVALID_REACTIVATION_LINK: 'INVALID_REACTIVATION_LINK',
  NETWORK_ERROR: 'NETWORK_ERROR',
  NOT_SCHEDULED: 'NOT_SCHEDULED',
  OAUTH_PROVIDER_REJECTED: 'OAUTH_PROVIDER_REJECTED',
  REACTIVATION_TOKEN_INVALID: 'REACTIVATION_TOKEN_INVALID',
  REACTIVATION_TOKEN_STALE: 'REACTIVATION_TOKEN_STALE',
  TOO_MANY_REQUEST: 'TOO_MANY_REQUEST',
} as const;

export const APPLE_PRIVATE_RELAY_EMAIL_NOT_ALLOWED_CODE =
  SIGN_IN_ERROR_CODES.APPLE_PRIVATE_RELAY_EMAIL_NOT_ALLOWED;

export type SignInErrorCode =
  (typeof SIGN_IN_ERROR_CODES)[keyof typeof SIGN_IN_ERROR_CODES];

const SIGN_IN_ERROR_CODE_VALUES = new Set<string>(
  Object.values(SIGN_IN_ERROR_CODES)
);

export interface ConsumedSignInErrorQuery {
  errorCode: SignInErrorCode | null;
  message: string | null;
  nextSearch: string | null;
}

export function normalizeSignInErrorCode(
  code: string | null | undefined
): SignInErrorCode | null {
  return code && SIGN_IN_ERROR_CODE_VALUES.has(code)
    ? (code as SignInErrorCode)
    : null;
}

export function getSignInErrorCodeFromError(
  error: unknown,
  fallback: SignInErrorCode = SIGN_IN_ERROR_CODES.AUTH_UNEXPECTED_ERROR
): SignInErrorCode {
  const friendly = UserFriendlyError.fromAny(error);
  const code = normalizeSignInErrorCode(
    typeof friendly.code === 'string' ? friendly.code : undefined
  );
  if (code) {
    return code;
  }

  const name = normalizeSignInErrorCode(
    typeof friendly.name === 'string' ? friendly.name : undefined
  );
  if (name) {
    return name;
  }

  if (
    friendly.message === 'Gateway Timeout' ||
    friendly.message.startsWith('Network error')
  ) {
    return SIGN_IN_ERROR_CODES.NETWORK_ERROR;
  }

  return fallback;
}

export function buildSignInErrorRedirect(errorCode: SignInErrorCode): string {
  const params = new URLSearchParams();
  params.set('error_code', errorCode);
  return `/sign-in?${params.toString()}`;
}

export function buildSignInErrorRedirectFromError(
  error: unknown,
  fallback?: SignInErrorCode
): string {
  return buildSignInErrorRedirect(getSignInErrorCodeFromError(error, fallback));
}

export function consumeSignInErrorQuery(
  searchParams: URLSearchParams
): ConsumedSignInErrorQuery {
  if (!searchParams.has('error') && !searchParams.has('error_code')) {
    return { errorCode: null, message: null, nextSearch: null };
  }

  const next = new URLSearchParams(searchParams);
  const errorCode = normalizeSignInErrorCode(next.get('error_code'));
  const message = next.get('error')?.trim() || null;
  next.delete('error');
  next.delete('error_code');

  return {
    errorCode,
    message,
    nextSearch: next.toString(),
  };
}
